// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Star, Edit, User, Building, Mail, Phone, Save, X, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

interface GraphicsVendorProfile {
  id: string;
  user_id: string;
  business_name?: string;
  description?: string;
  contact_phone?: string;
  rating_average?: number;
  review_count?: number;
  featured_image?: string;
  bio?: string;
  created_at: string;
  updated_at: string;
}

interface ReviewData {
  id: string;
  rating: number;
  review_text: string;
  created_at: string;
  reviewer: {
    name: string;
  };
}

const VendorProfile: React.FC = () => {
  const { user } = useAuth();
  const [vendorProfile, setVendorProfile] = useState<GraphicsVendorProfile | null>(null);
  const [reviews, setReviews] = useState<ReviewData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editForm, setEditForm] = useState({
    business_name: '',
    description: '',
    contact_phone: '',
    bio: '',
    venmo_handle: '',
    featured_image: ''
  });

  useEffect(() => {
    fetchVendorProfile();
    fetchReviews();
  }, [user?.id]);

  const fetchVendorProfile = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      
      // Get vendor profile and user's venmo_handle separately
      const [vendorResult, profileResult] = await Promise.all([
        supabase
          .from('graphics_vendor_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('profiles')
          .select('venmo_handle')
          .eq('user_id', user.id)
          .single()
      ]);

      if (vendorResult.error) throw vendorResult.error;
      
      const vendorData = vendorResult.data;
      const venmoHandle = profileResult.data?.venmo_handle || '';
      
      setVendorProfile(vendorData);
      
      // Initialize edit form with current data
      if (vendorData) {
        setEditForm({
          business_name: vendorData.business_name || '',
          description: vendorData.description || '',
          contact_phone: vendorData.contact_phone || '',
          bio: vendorData.bio || '',
          venmo_handle: venmoHandle,
          featured_image: vendorData.featured_image || ''
        });
      }
    } catch (error) {
      console.error('Error fetching vendor profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    if (!user?.id) return;

    try {
      // First get the vendor profile ID
      const { data: profileData, error: profileError } = await supabase
        .from('graphics_vendor_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profileData) return;

      const { data, error } = await supabase
        .from('graphics_vendor_reviews')
        .select(`
          id,
          rating,
          review_text,
          created_at,
          profiles!graphics_vendor_reviews_reviewer_id_fkey (
            name
          )
        `)
        .eq('vendor_id', profileData.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const formattedReviews = data?.map(review => ({
        ...review,
        reviewer: {
          name: (review.profiles as any)?.name || 'Anonymous'
        }
      })) || [];
      
      setReviews(formattedReviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      
      // Update venmo_handle in profiles table
      console.log('🔍 Updating venmo_handle in profiles table:', editForm.venmo_handle);
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          venmo_handle: editForm.venmo_handle || null,
        })
        .eq('user_id', user.id);

      if (profileError) {
        console.error('❌ Failed to update venmo_handle:', profileError);
        throw new Error(`Profile update failed: ${profileError.message}`);
      }
      
      if (vendorProfile) {
        // Update existing profile (without venmo_handle)
        const vendorUpdateData = {
          business_name: editForm.business_name,
          description: editForm.description,
          contact_phone: editForm.contact_phone,
          bio: editForm.bio,
          featured_image: editForm.featured_image,
          updated_at: new Date().toISOString()
        };
        console.log('🔍 Updating graphics vendor profile with data:', vendorUpdateData);
        
        const { error } = await supabase
          .from('graphics_vendor_profiles')
          .update(vendorUpdateData)
          .eq('user_id', user.id);

        if (error) {
          console.error('❌ Graphics vendor profile update error:', error);
          throw error;
        }
        console.log('✅ Graphics vendor profile updated successfully');
      } else {
        // Create new profile (without venmo_handle)
        const vendorInsertData = {
          user_id: user.id,
          business_name: editForm.business_name,
          description: editForm.description,
          contact_phone: editForm.contact_phone,
          bio: editForm.bio,
          featured_image: editForm.featured_image
        };
        console.log('🔍 Creating graphics vendor profile with data:', vendorInsertData);
        
        const { error } = await supabase
          .from('graphics_vendor_profiles')
          .insert(vendorInsertData);

        if (error) {
          console.error('❌ Graphics vendor profile creation error:', error);
          throw error;
        }
        console.log('✅ Graphics vendor profile created successfully');
      }

      toast.success('Profile updated successfully!');
      setIsEditing(false);
      fetchVendorProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset form to original values
    if (vendorProfile) {
      setEditForm({
        business_name: vendorProfile.business_name || '',
        description: vendorProfile.description || '',
        contact_phone: vendorProfile.contact_phone || '',
        bio: vendorProfile.bio || '',
        venmo_handle: editForm.venmo_handle,
        featured_image: vendorProfile.featured_image || ''
      });
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    try {
      setUploadingImage(true);
      
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/featured-image-${Date.now()}.${fileExt}`;
      
      // Upload file to Supabase Storage
      const { data, error } = await supabase.storage
        .from('graphics-vendor-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload image: ' + error.message);
        return;
      }

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('graphics-vendor-images')
        .getPublicUrl(fileName);

      if (publicUrlData.publicUrl) {
        // Update form state with new image URL
        setEditForm(prev => ({
          ...prev,
          featured_image: publicUrlData.publicUrl
        }));
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Loading profile...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Your Graphics Profile</h1>
          <p className="text-muted-foreground">
            Manage your graphics business information and settings
          </p>
        </div>
        {!isEditing ? (
          <Button onClick={() => setIsEditing(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button onClick={handleSaveProfile} disabled={loading}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
            <Button variant="outline" onClick={handleCancelEdit}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        )}
      </div>

      {vendorProfile ? (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Featured Image & Business Information */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Business Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Featured Image */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Featured Image</label>
                  {isEditing ? (
                    <div className="space-y-3">
                      {/* Image Preview */}
                      {editForm.featured_image && (
                        <div className="relative">
                          <img
                            src={editForm.featured_image}
                            alt="Featured image preview"
                            className="w-full h-48 object-cover rounded-lg border"
                          />
                        </div>
                      )}
                      
                      {/* File Upload */}
                      <div className="flex items-center gap-3">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="hidden"
                          id="featured-image-upload"
                        />
                        <label
                          htmlFor="featured-image-upload"
                          className="flex items-center gap-2 px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md cursor-pointer text-sm font-medium disabled:opacity-50"
                        >
                          {uploadingImage ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                          {uploadingImage ? 'Uploading...' : 'Upload Image'}
                        </label>
                        
                        {editForm.featured_image && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditForm(prev => ({ ...prev, featured_image: '' }))}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      
                      {/* URL Input as Alternative */}
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Or enter image URL</label>
                        <Input
                          value={editForm.featured_image}
                          onChange={(e) => setEditForm({...editForm, featured_image: e.target.value})}
                          placeholder="https://example.com/image.jpg"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      {vendorProfile.featured_image ? (
                        <div className="relative">
                          <img
                            src={vendorProfile.featured_image}
                            alt="Featured business image"
                            className="w-full h-48 object-cover rounded-lg border"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-48 border-2 border-dashed border-muted rounded-lg">
                          <div className="text-center">
                            <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">No featured image set</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Business Information */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Business Name</label>
                    {isEditing ? (
                      <Input
                        value={editForm.business_name}
                        onChange={(e) => setEditForm({...editForm, business_name: e.target.value})}
                        placeholder="Enter business name"
                      />
                    ) : (
                      <p className="text-lg font-semibold">
                        {vendorProfile.business_name || 'Not set'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    {isEditing ? (
                      <Textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                        placeholder="Describe your graphics services"
                        rows={3}
                      />
                    ) : (
                      <p className="text-sm">
                        {vendorProfile.description || 'No description provided'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Contact Phone</label>
                    {isEditing ? (
                      <Input
                        value={editForm.contact_phone}
                        onChange={(e) => setEditForm({...editForm, contact_phone: e.target.value})}
                        placeholder="Enter contact phone"
                      />
                    ) : (
                      <p className="text-sm">
                        {vendorProfile.contact_phone || 'Not set'}
                      </p>
                    )}
                  </div>
                  {!isEditing && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Member Since</label>
                      <p className="text-sm">
                        {new Date(vendorProfile.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Performance Metrics & Reviews */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5" />
                Performance & Reviews
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Average Rating</span>
                <div className="flex items-center gap-1">
                  {vendorProfile.rating_average ? (
                    <>
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold">{vendorProfile.rating_average.toFixed(1)}</span>
                    </>
                  ) : (
                    <Badge variant="secondary">No ratings yet</Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Total Reviews</span>
                <Badge variant="outline">
                  {vendorProfile.review_count || 0} reviews
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground">
                {vendorProfile.review_count === 0 
                  ? "Complete your first order to start receiving reviews"
                  : "Keep up the great work!"}
              </div>
              
              {/* Recent Reviews */}
              {reviews.length > 0 && (
                <div className="mt-4 space-y-3">
                  <h4 className="text-sm font-medium">Recent Reviews</h4>
                  {reviews.slice(0, 3).map((review) => (
                    <div key={review.id} className="border-l-2 border-primary pl-3 space-y-1">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3 w-3 ${
                              i < review.rating 
                                ? 'fill-yellow-400 text-yellow-400' 
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                        <span className="text-xs text-muted-foreground ml-1">
                          by {review.reviewer.name}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        "{review.review_text}"
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Account Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{user?.email}</span>
              </div>
              {vendorProfile.contact_phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{vendorProfile.contact_phone}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Graphics Design Vendor</span>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Profile ID</label>
                <p className="text-xs font-mono text-muted-foreground">{vendorProfile.id}</p>
              </div>
            </CardContent>
          </Card>

          {/* Profile Completion */}
          <Card>
            <CardHeader>
              <CardTitle>Profile Completion</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span>Featured Image</span>
                  <Badge variant={vendorProfile.featured_image ? "default" : "secondary"}>
                    {vendorProfile.featured_image ? "Complete" : "Missing"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Business Name</span>
                  <Badge variant={vendorProfile.business_name ? "default" : "secondary"}>
                    {vendorProfile.business_name ? "Complete" : "Missing"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Contact Phone</span>
                  <Badge variant={vendorProfile.contact_phone ? "default" : "secondary"}>
                    {vendorProfile.contact_phone ? "Complete" : "Missing"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Description</span>
                  <Badge variant={vendorProfile.description ? "default" : "secondary"}>
                    {vendorProfile.description ? "Complete" : "Missing"}
                  </Badge>
                </div>
                <div className="pt-2">
                  <div className="text-xs text-muted-foreground mb-1">
                    Profile Completion
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300" 
                      style={{ 
                        width: `${[
                          vendorProfile.featured_image,
                          vendorProfile.business_name,
                          vendorProfile.contact_phone,
                          vendorProfile.description,
                        ].filter(Boolean).length / 4 * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Profile Found</h3>
            <p className="text-muted-foreground text-center mb-4">
              You haven't set up your graphics vendor profile yet. Click the button below to get started.
            </p>
            <Button onClick={() => setIsEditing(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Create Profile
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VendorProfile;